import React, { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Box } from '@chakra-ui/core'
import { css } from '@emotion/core'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import useLocaleText from 'components/useLocaleText'
import {
  RichTextEditor,
  EditorContent,
  getRichTextByteSize,
  MAX_RICH_TEXT_BYTES,
} from 'components/CommonComponents/RichTextEditor'
import { ErrorMessage } from 'components/ui/Input/ErrorMessage'
import { ClearContentButton } from 'components/TasksV2/UI/ClearContentButton'
import { useBuilderWidget } from 'components/TasksV2/TaskCreation/contexts/BuilderWidgetContext'
import { useCreationText } from 'components/TasksV2/TaskCreation/hooks/useCreationText'

const clearBtnCss = css`
  .clear-btn {
    top: 41px;
  }
`

interface ImageDescriptionProps {
  subtaskPath: string
  minHeight?: string | number
  placeholder?: string
  onFocus?: () => void
  onBlur?: () => void
  onClear?: () => void
  showClear?: boolean
}

export const ImageDescription: React.FC<ImageDescriptionProps> = ({
  subtaskPath,
  minHeight = '120px',
  placeholder,
  onFocus,
  onBlur,
  onClear,
  showClear = false,
}) => {
  const { control, getValues, watch } = useFormContext()
  const builder = useBuilderWidget()
  const creationText = useCreationText()
  const isRtl = useSelector(isRtlSelector)

  const t_description_placeholder = useLocaleText('t_say_something') || 'Add description...'

  const t_placeholder = useLocaleText(placeholder || '')
  const t_text_too_long = useLocaleText('text_too_long')

  const richTextValue = watch(`${subtaskPath}.content.rich_text`) as EditorContent | null

  const richTextError = useMemo(() => {
    const bytes = getRichTextByteSize(richTextValue)

    return bytes > MAX_RICH_TEXT_BYTES ? t_text_too_long || 'Text is too long (max 4KB)' : undefined
  }, [richTextValue, t_text_too_long])

  return (
    <>
      {builder && (
        <div className="creation-field-label creation-instructions-label">{creationText.photoInstructions}</div>
      )}
      <Box pos="relative" css={showClear ? clearBtnCss : undefined}>
        <Controller
          name={`${subtaskPath}.content.rich_text`}
          control={control}
          as={RichTextEditor}
          toolbarVisibility="always"
          defaultValue={(getValues(`${subtaskPath}.content.rich_text`) as EditorContent) || null}
          placeholder={placeholder ? t_placeholder : t_description_placeholder}
          contentMinHeight={minHeight}
          onFocus={onFocus}
          onBlur={onBlur}
          containerStyle={{
            border: builder ? '1px solid #e2e5e8' : 'none',
            ...(builder ? { minHeight: '100px' } : {}),
            borderRadius: '12px',
            boxShadow: richTextError ? '0px 0px 0px 3px #EA9999' : 'none',
          }}
          rules={{
            validate: (value: EditorContent) => {
              const bytes = getRichTextByteSize(value)

              return bytes <= MAX_RICH_TEXT_BYTES || t_text_too_long || 'Text is too long (max 4KB)'
            },
          }}
        />
        {showClear && onClear && (
          <Box
            className="clear-btn"
            pos="absolute"
            right={isRtl ? '0' : '-10px'}
            left={isRtl ? '-10px' : '0'}
            zIndex={5}
          >
            <ClearContentButton onClick={onClear} />
          </Box>
        )}
      </Box>
      {richTextError && (
        <Box px="10px">
          <ErrorMessage errorMessage={richTextError} />
        </Box>
      )}
    </>
  )
}
